import { pdf } from '@react-pdf/renderer';

/**
 * Render a react-pdf document and put the browser's print dialog in front of it.
 *
 * The PDF is loaded into an off-screen iframe rather than a new tab, for two
 * reasons: a tab opened after an `await` has lost the user gesture and gets
 * blocked, and an iframe lets us call print() ourselves instead of asking the
 * user to find Ctrl+P. The iframe has to outlive the call — tearing it down
 * while the dialog is open cancels the job — so it is removed on the dialog's
 * afterprint, with a long timer as the backstop for browsers that never fire it.
 *
 * Resolves 'printed' when the dialog was opened, 'opened' when the document
 * was shown in a tab, or 'downloaded' when downloaded directly.
 * Throws only if the PDF itself could not be produced.
 */
export default async function printInvoice(element, { forceTab = false, downloadName = null } = {}) {
  const blob = await pdf(element).toBlob();
  const url = URL.createObjectURL(blob);

  // Large multi-page documents (or callers requesting forceTab) bypass the hidden
  // iframe to prevent Chromium's virtualized iframe rendering from dropping pages past 30-40.
  if (forceTab) {
    const win = window.open(url, '_blank');
    if (win) {
      setTimeout(() => URL.revokeObjectURL(url), 120000);
      return 'opened';
    }
    // If pop-up is blocked by browser, download the PDF directly.
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadName || 'invoices.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 120000);
    return 'downloaded';
  }

  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  frame.src = url;

  const removeFrame = () => {
    if (frame.parentNode) frame.parentNode.removeChild(frame);
  };
  const cleanup = () => {
    removeFrame();
    URL.revokeObjectURL(url);
  };

  return new Promise((resolve) => {
    frame.onload = () => {
      try {
        const win = frame.contentWindow;
        // Safari in particular refuses to print a cross-document PDF this way,
        // and throws rather than failing quietly.
        win.addEventListener('afterprint', cleanup, { once: true });
        win.focus();
        win.print();
        // Backstop: some browsers never fire afterprint for an embedded PDF.
        setTimeout(cleanup, 120000);
        resolve('printed');
      } catch {
        // The frame goes, but the url has to survive for the tab to load it.
        removeFrame();
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 120000);
        resolve('opened');
      }
    };

    document.body.appendChild(frame);
  });
}
